import { getInsuranceTypeDisplayName, PetCoverage } from '@/interfaces';
import { ReferenceDataServices as _refData } from '@/services/store/reference-data.services';
import {
  getAdditionalText,
  getPdsOrSpdsVersion,
  InsuranceDocumentType,
  policyDetailsCheckIcon,
} from './PolicyDetailsHelperMapper';
import { replaceBookmark } from '@/utils/bookmark.replace';

/**
 * Returns the populated SummaryItems with DOM ready text.
 * @param coverage The Pet insurance coverage
 * @param content The props.content for the page
 */
export async function mapAsPetCoverage(
  coverage: PetCoverage,
  content: any,
  dictionary: Record<string, string>
): Promise<any> {
  const summary = {} as any;
  // Policy details
  summary.insuranceTypeDisplayName = getInsuranceTypeDisplayName(dictionary, coverage.coverageCode);
  summary.petType = getPetType(coverage);
  summary.nameAndBreed = `${coverage.petName}, ${coverage.petBreed}`;
  summary.age = getPetAge(coverage);
  summary.petLocation = coverage.petLocationDescription;

  // Applicable excesses
  const petBasicExcess = await getPetExcess(coverage);
  summary.basicExcess = petBasicExcess;

  // Optional benefits
  summary.tenderLovingCare = getTenderLovingCare(coverage, petBasicExcess, content);

  // Insurance documents
  summary.pdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.PDS);
  summary.spdsVersion = await getPdsOrSpdsVersion(coverage, InsuranceDocumentType.SPDS);

  return summary;
}

async function getPetExcess(coverage: PetCoverage): Promise<string> {
  try {
    const response = await _refData.getPetExcessOptions(coverage.coverageCode);
    if (response?.values?.length > 0) {
      const result = response.values.find((x: any) => x.code === coverage.basicExcessCode)?.name;
      return result || '';
    }
  } catch (error) {
    console.error('error', error);
  }
  return '';
}

function getPetAge(coverage: PetCoverage): string {
  const now = new Date();
  const birthDate = new Date(coverage.dateOfBirth);
  const diffInMonths = (now.getFullYear() - birthDate.getFullYear()) * 12 + now.getMonth() - birthDate.getMonth();

  if (diffInMonths <= 11) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'}`;
  } else {
    const years = Math.floor(diffInMonths / 12);
    return `${years} year${years === 1 ? '' : 's'}`;
  }
}

function getTenderLovingCare(coverage: PetCoverage, petBasicExcess: string, content: any): string | null {
  if (coverage.hasTlcCover) {
    const tlcText = getAdditionalText(content, 'optionalBenefitsTenderLovingCareText');
    return policyDetailsCheckIcon + 'Added' + replaceBookmark('petBasicExcess', tlcText, petBasicExcess);
  }
  return null;
}

function getPetType(coverage: PetCoverage): string {
  return coverage.petType ?? 'Unknown';
}
